/**
 * Crash reporting for the browser AND the desktop shell.
 *
 * One file for two surfaces because they are one bundle — `runtime.ts` is the
 * only module that knows the difference, and it is the module consulted here
 * to decide which surface a report claims to come from. A crash in the Tauri
 * webview is a `desktop` report; the identical code in a tab is a `web` one,
 * and telling them apart matters because one of them has a Rust process
 * underneath it that can fail in ways a browser cannot.
 *
 * Three entry points, because the browser has three ways to lose an error and
 * an app that only catches React's misses two of them:
 *
 * - **`ErrorBoundary`** catches errors thrown while rendering.
 * - **`window.onerror`** catches everything thrown outside React — an event
 *   handler, a `setTimeout`, a script that failed to parse.
 * - **`unhandledrejection`** catches a promise nobody attached a `.catch` to,
 *   which in an app this async is the largest of the three categories.
 */
import { Reporter } from '@aegis/api-client';
import { isDesktop, platform, url } from './runtime.js';
import { takePendingPanics } from './desktop.js';

/** Set at build time by Vite. `dev` when running from source. */
const APP_VERSION: string = import.meta.env.VITE_APP_VERSION ?? 'dev';
const BUILD_ID: string | null = import.meta.env.VITE_BUILD_ID ?? null;

/**
 * The route as a TEMPLATE, never as a path.
 *
 * `/journal/42` in this field would make it unbounded — useless for grouping,
 * and a slow leak of which trades exist. The ids this app puts in a URL are
 * opaque prefixed strings (`trd_…`, `acct_…`), so the rule is: a segment that
 * looks like an id becomes `:id`.
 */
export function routeTemplate(pathname: string): string {
  return pathname
    .split('/')
    .map((seg) => (/^(trd|acct|str|rep|alert|dr)_|^\d+$|^[0-9a-f-]{16,}$/i.test(seg) ? ':id' : seg))
    .join('/') || '/';
}

let reporter: Reporter | null = null;

export function crashReporter(): Reporter {
  reporter ??= new Reporter({
    url,
    context: {
      surface: isDesktop() ? 'desktop' : 'web',
      appVersion: APP_VERSION,
      buildId: BUILD_ID,
      platform: platform(),
      osVersion: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 120) : null,
    },
    // Asked at crash time, not at construction. This reporter is built before
    // the first navigation, so a captured value would say `/` forever.
    route: () => (typeof location === 'undefined' ? null : routeTemplate(location.pathname)),
    // Off in development: the error is already on screen with a better stack
    // than this would carry, and filing it puts noise in front of whoever
    // reads the real list tomorrow.
    enabled: import.meta.env.PROD,
  });
  return reporter;
}

/**
 * Attach the two handlers React cannot provide. Idempotent.
 *
 * Called once from `main.tsx`, before the app renders — an error thrown during
 * the first render is exactly the one worth catching, and a listener attached
 * afterwards would miss it.
 */
let attached = false;
export function installGlobalHandlers(): void {
  if (attached || typeof window === 'undefined') return;
  attached = true;
  const r = crashReporter();

  window.addEventListener('error', (e: ErrorEvent) => {
    // `e.error` is the thrown value; `e.message` is all there is for a
    // cross-origin script error, where the browser withholds the rest.
    void r.report(e.error ?? e.message, 'unhandled');
  });

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    void r.report(e.reason, 'rejection');
  });

  // Navigation breadcrumbs. The route at the moment of the crash is on the
  // report; how the user got there usually explains it.
  //
  // The crumb is recorded AFTER the navigation, not before. The first version
  // did it the other way round and produced a trail shifted by one — every
  // entry naming the screen being left rather than the one being entered, and
  // the last navigation, into the screen that actually crashed, missing
  // entirely. A test asserting the crumb's value caught it; a test asserting
  // only that a crumb existed would not have.
  const push = history.pushState.bind(history);
  history.pushState = (...args: Parameters<typeof history.pushState>) => {
    const result = push(...args);
    r.breadcrumb('navigation', routeTemplate(location.pathname));
    return result;
  };
  window.addEventListener('popstate', () => {
    r.breadcrumb('navigation', `back to ${routeTemplate(location.pathname)}`);
  });
}

/**
 * Deliver panics the Rust shell recorded before it died.
 *
 * A panic in the shell is not a JavaScript error and no boundary can see it;
 * the release profile aborts rather than unwinds, so the process is simply
 * gone. `crash.rs` writes a line to disk instead, and this is where that line
 * turns into a report — on the launch after the one that crashed, which is the
 * launch the user makes anyway.
 *
 * Three things are overridden rather than taken from the running app:
 *
 * - **`occurredAt`**, because the panic happened before this process started.
 * - **`appVersion`**, because the user may have updated in between, and "which
 *   version panicked" is the whole question.
 * - **`platform`**, which is the only field this build can still answer.
 *
 * A no-op on the web, where `takePendingPanics` returns an empty list.
 */
export async function flushDesktopPanics(): Promise<number> {
  if (!isDesktop()) return 0;
  let sent = 0;
  try {
    for (const p of await takePendingPanics()) {
      // Shaped as an error so `normalizeError` treats it like any other. The
      // "stack" is one synthetic frame — a panic location is all Rust hands
      // over without a backtrace feature compiled in — and it is what the
      // fingerprint groups on, so two panics from the same `expect` land
      // together.
      await crashReporter().report(
        {
          name: 'Panic',
          message: p.message,
          stack: `Panic: ${p.message}\n    at ${p.location} (thread ${p.thread})`,
        },
        'panic',
        {
          occurredAt: new Date(p.occurredAt),
          context: { appVersion: p.appVersion },
        },
      );
      sent += 1;
    }
  } catch {
    // The shell is not obliged to have this command — an older binary running
    // a newer bundle does not — and a missing panic report must not stop the
    // app starting.
  }
  return sent;
}
